import { useState } from "react";
import { Mail, Phone, MapPin, Send, Clock, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully! We'll get back to you soon.");
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (error) {
      toast.error("Unable to send message. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-20" data-testid="contact-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-teal-700 font-medium tracking-widest uppercase text-sm mb-4">
              Get In Touch
            </p>
            <h1 className="font-serif text-5xl md:text-6xl font-bold text-slate-900 leading-[1.1] mb-6">
              Contact <span className="text-amber-700 italic">Us</span>
            </h1>
            <p className="text-slate-600 text-lg md:text-xl leading-relaxed">
              Have questions about our programs, events, or how you can get involved? We'd love to hear from you.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-slate-50" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <Card className="shadow-xl border-0">
              <CardContent className="p-8 md:p-10">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                    <MessageSquare className="w-6 h-6 text-teal-700" />
                  </div>
                  <div>
                    <h2 className="font-serif text-2xl font-semibold text-slate-900">Send a Message</h2>
                    <p className="text-slate-500 text-sm">We'll respond within 24-48 hours</p>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name" className="text-sm font-medium text-slate-700">
                        Your Name
                      </Label>
                      <Input
                        id="name"
                        type="text"
                        placeholder="Full name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="mt-1 h-12"
                        data-testid="contact-name-input" />

                    </div>
                    <div>
                      <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                        Email Address
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="your@email.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="mt-1 h-12"
                        data-testid="contact-email-input" />

                    </div>
                  </div>

                  <div>
                    <Label htmlFor="subject" className="text-sm font-medium text-slate-700">
                      Subject
                    </Label>
                    <Input
                      id="subject"
                      type="text"
                      placeholder="What is this regarding?"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      required
                      className="mt-1 h-12"
                      data-testid="contact-subject-input" />

                  </div>

                  <div>
                    <Label htmlFor="message" className="text-sm font-medium text-slate-700">
                      Message
                    </Label>
                    <Textarea
                      id="message"
                      placeholder="Tell us how we can help you..."
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      required
                      rows={6}
                      className="mt-1 resize-none"
                      data-testid="contact-message-input" />

                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white py-6 font-sans font-bold uppercase text-sm"
                    data-testid="contact-submit-btn">

                    {loading ?
                    <span className="flex items-center gap-2">
                        <div className="spinner"></div>
                        Sending...
                      </span> :

                    <span className="flex items-center gap-2">
                        <Send className="w-4 h-4" />
                        Send Message
                      </span>
                    }
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="font-serif text-3xl font-semibold text-slate-900 mb-6">
                  Contact Information
                </h2>
                <p className="text-slate-600 leading-relaxed mb-8">
                  Whether you're a patient seeking support, a donor wanting to contribute, or someone interested in partnering with us, we're here to help.
                </p>
              </div>

              <div className="space-y-6">
                <Card className="border-slate-100">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-teal-700" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Office Location</h3>
                      <p className="text-slate-600">
                        4014 Market Street

                        <br />
                        Houston, Texas
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-slate-100">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-amber-700" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Phone</h3>
                      <a href="tel:+17135555555" className="hover:text-teal-800 !font-medium text-teal-700">(832) 252-9293</a>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-slate-100">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-slate-700" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Email</h3>
                      <a href="mailto:info@sicklecellhouston.org" className="hover:text-teal-800 !font-medium text-teal-700">care@sicklecellhouston.org

                      </a>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-slate-100">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-teal-700" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Office Hours</h3>
                      <p className="text-slate-600">
                        Monday - Friday: 9:00 AM - 5:00 PM<br />
                        Saturday & Sunday: Closed
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section Placeholder */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="bg-slate-100 rounded-sm h-80 flex items-center justify-center">
            <div className="text-center">
              <MapPin className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-500 font-medium">Serving the Greater Houston Area</p>
              <p className="text-slate-400 text-sm">Houston, Texas</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Preview */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-3xl font-semibold text-slate-900 mb-6">
            Frequently Asked Questions
          </h2>
          <p className="text-slate-600 mb-8">
            Can't find what you're looking for? Send us a message and we'll be happy to help.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
            <Card className="border-slate-100">
              <CardContent className="p-6">
                <h4 className="font-semibold text-slate-900 mb-2">How can I become a member?</h4>
                <p className="text-slate-600 text-sm">
                  Visit our Members page to sign up for free. Membership gives you access to events, resources, and our community.
                </p>
              </CardContent>
            </Card>
            <Card className="border-slate-100">
              <CardContent className="p-6">
                <h4 className="font-semibold text-slate-900 mb-2">Is my donation tax-deductible?</h4>
                <p className="text-slate-600 text-sm">
                  Yes! We are a 501(c)(3) nonprofit organization. All donations are tax-deductible to the fullest extent allowed by law.
                </p>
              </CardContent>
            </Card>
            <Card className="border-slate-100">
              <CardContent className="p-6">
                <h4 className="font-semibold text-slate-900 mb-2">How can I volunteer?</h4>
                <p className="text-slate-600 text-sm">
                  We're always looking for volunteers! Contact us to learn about current opportunities.
                </p>
              </CardContent>
            </Card>
            <Card className="border-slate-100">
              <CardContent className="p-6">
                <h4 className="font-semibold text-slate-900 mb-2">Do you offer patient assistance?</h4>
                <p className="text-slate-600 text-sm">
                  Yes, we provide various support services for sickle cell patients. Reach out to discuss how we can help.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>);

};

export default ContactPage;